// 纯函数工具 —— 从 EpisodeStoryboardBoardWorkbench.jsx 抽出
// 不依赖 React，不依赖业务 hooks，可直接 import 使用。

import { API_HOST } from "../../../utils/constants";
import { getSeedance25ReferenceMediaType, getSeedance25ReferenceUrl } from "../../../utils/seedance25Capabilities";
import { isDomesticOfficialSeedance } from '../../../utils/domesticSeedanceCapabilities';
import {
  BOARD_COUNT,
  BOARD_SECONDS,
  GUA_VIDEO_RESOLUTION,
  DEFAULT_VIDEO_LIGHTING,
  DEFAULT_VIDEO_RATIO,
  VIDEO_REF_LIMIT,
} from "./constants";
import { applyGuaVoiceBindingPrompt } from "../../../utils/guaVoiceBindings";
import { getEffectiveAssetMeta } from "../../../utils/guaVideoAssetStatus";
import { requireDreaminaProxyOriginalUrl } from "../../../utils/dreaminaProxyReferences";
import {
  normalizeVideoProvider,
  VIDEO_PROVIDER_ENG_GUAVIDEO,
  VIDEO_PROVIDER_GUAVIDEO,
  VIDEO_PROVIDER_OMINI_SEEDANCE,
} from "../../../utils/projectRuntime";

// ============================================================
// 基础字符串
// ============================================================

export const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

export const normalizePresentationMode = (mode) => {
  const value = firstText(mode).toLowerCase();
  if (["narration", "narrated", "commentary", "explain", "解说剧"].includes(value)) return "narration";
  if (["blocking", "fuzzy", "rough", "wireframe", "框架故事板", "模糊故事板", "模糊站位", "线稿站位"].includes(value)) return "blocking";
  if (["grid9", "ninegrid", "nine-grid", "9grid", "九宫格", "九宫格故事板", "连续九宫格"].includes(value)) return "grid9";
  return "formal";
};

export const parseChineseNumber = (value) => {
  const text = firstText(value).replace(/两/g, "二");
  if (!text) return null;
  if (/^\d+$/.test(text)) return Number(text);
  const digits = { 一: 1, 二: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9 };
  if (text === "十") return 10;
  if (text.startsWith("十")) return 10 + (digits[text[1]] || 0);
  if (text.endsWith("十")) return (digits[text[0]] || 0) * 10;
  if (text.includes("十")) {
    const [tens, ones] = text.split("十");
    return (digits[tens] || 1) * 10 + (digits[ones] || 0);
  }
  return digits[text] || null;
};

export const detectDirectorStoryboardIntent = (requestText, boardCount = BOARD_COUNT) => {
  const text = firstText(requestText);
  if (!text) return { mode: "note", boardIndex: null };
  if (/(整集|全部|所有|全都|全量|重排|重新生成全部|重新生成所有|全部重新生成|重新生成\s*(?:10|十)\s*(?:张|个))/.test(text)) {
    return { mode: "all", boardIndex: null };
  }
  const digitMatch = text.match(/第?\s*(\d{1,2})\s*(?:张|个|段|页|板|号|幅)(?:故事板)?/)
    || text.match(/(?:故事板|storyboard|board)\s*#?\s*(\d{1,2})/i);
  const chineseMatch = text.match(/第?\s*([一二三四五六七八九十两]{1,3})\s*(?:张|个|段|页|板|号|幅)(?:故事板)?/);
  const boardIndex = digitMatch ? Number(digitMatch[1]) : parseChineseNumber(chineseMatch?.[1]);
  const maxBoardIndex = Number(boardCount) > 0 ? Number(boardCount) : BOARD_COUNT;
  if (Number.isInteger(boardIndex) && boardIndex >= 1 && boardIndex <= maxBoardIndex) {
    return { mode: "single", boardIndex };
  }
  return { mode: "note", boardIndex: null };
};

// ============================================================
// 图片 URL / 资产图
// ============================================================

export const normalizeImageUrl = (url) => {
  const value = firstText(url);
  if (!value) return "";
  if (value.startsWith("http") || value.startsWith("data:")) return value;
  if (value.startsWith("/")) return `${API_HOST}${value}`;
  return value;
};

export const parseReferenceImages = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const imageFromRefItem = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item;
  return firstText(item.url, item.imageUrl, item.base64, item.src);
};

export const getAssetImageUrl = (asset) => {
  const main = normalizeImageUrl(asset?.imageUrl);
  if (main) return main;
  const fromRefs = parseReferenceImages(asset?.referenceImages).map(imageFromRefItem).map(normalizeImageUrl).find(Boolean);
  if (fromRefs) return fromRefs;
  const fromVariation = (asset?.variations || []).map((item) => normalizeImageUrl(item?.imageUrl)).find(Boolean);
  return fromVariation || "";
};

export const getAssetGuaVideoMeta = (asset, imageUrl) => {
  const normalizedUrl = normalizeImageUrl(imageUrl);
  const mainUrl = normalizeImageUrl(asset?.imageUrl);
  if (normalizedUrl && mainUrl === normalizedUrl) {
    return {
      guaVideoAssetId: firstText(asset?.guaVideoAssetId),
      guaVideoAssetStatus: firstText(asset?.guaVideoAssetStatus),
      engGuaVideoAssetId: firstText(asset?.engGuaVideoAssetId),
      engGuaVideoAssetStatus: firstText(asset?.engGuaVideoAssetStatus),
      guaOminiAssetId: firstText(asset?.guaOminiAssetId),
      guaOminiAssetStatus: firstText(asset?.guaOminiAssetStatus),
      dreaminaProxyAssetId: firstText(asset?.dreaminaProxyAssetId),
      dreaminaProxyAssetStatus: firstText(asset?.dreaminaProxyAssetStatus),
      assetProvider: firstText(asset?.assetProvider, asset?.provider),
    };
  }
  const matchedVariation = (asset?.variations || []).find((item) => (
    normalizeImageUrl(item?.imageUrl) === normalizedUrl
  ));
  return {
    guaVideoAssetId: firstText(matchedVariation?.guaVideoAssetId),
    guaVideoAssetStatus: firstText(matchedVariation?.guaVideoAssetStatus),
    engGuaVideoAssetId: firstText(matchedVariation?.engGuaVideoAssetId),
    engGuaVideoAssetStatus: firstText(matchedVariation?.engGuaVideoAssetStatus),
    guaOminiAssetId: firstText(matchedVariation?.guaOminiAssetId),
    guaOminiAssetStatus: firstText(matchedVariation?.guaOminiAssetStatus),
    dreaminaProxyAssetId: firstText(matchedVariation?.dreaminaProxyAssetId),
    dreaminaProxyAssetStatus: firstText(matchedVariation?.dreaminaProxyAssetStatus),
    assetProvider: firstText(matchedVariation?.assetProvider, matchedVariation?.provider),
  };
};

// ============================================================
// Asset 文本搜索
// ============================================================

export const collectTextValues = (value) => {
  const result = [];
  const visit = (item) => {
    if (item === null || item === undefined) return;
    if (typeof item === "string" || typeof item === "number") {
      result.push(String(item));
      return;
    }
    if (Array.isArray(item)) {
      item.forEach(visit);
      return;
    }
    if (typeof item === "object") {
      Object.values(item).forEach(visit);
    }
  };
  visit(value);
  return result;
};

export const assetTokens = (asset) => {
  const values = [
    asset?.name,
    asset?.aliases,
    asset?.description,
    asset?.imagePrompt,
    asset?.image_prompt,
    asset?.prompt,
    asset?.appearance,
    asset?.identity,
    asset?.role,
    asset?.type,
    asset?.style,
    asset?.tags,
    asset?.metadata,
  ]
    .flatMap(collectTextValues)
    .flatMap((value) => value.split(/[,\s，、/|;；]+/))
    .map((value) => value.trim())
    .filter((value) => value.length >= 2);
  return [...new Set(values)];
};

export const assetMatchesText = (asset, text) => {
  const source = firstText(text).toLowerCase();
  if (!source) return false;
  return assetTokens(asset).some((token) => source.includes(token.toLowerCase()));
};

export const assetMatchesQuery = (asset, query) => {
  const value = firstText(query).toLowerCase();
  if (!value) return true;
  return assetTokens(asset).some((token) => token.toLowerCase().includes(value));
};

// ============================================================
// localStorage memory
// ============================================================

export const storageKey = (projectId, episodeId) => `episode_storyboard_board_${projectId}_${episodeId}`;

export const loadMemory = (projectId, episodeId) => {
  try {
    const raw = window.localStorage.getItem(storageKey(projectId, episodeId));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

export const saveMemory = (projectId, episodeId, payload) => {
  try {
    window.localStorage.setItem(storageKey(projectId, episodeId), JSON.stringify(payload));
  } catch {
    // 本地记忆失败不影响生成。
  }
};

// ============================================================
// 参考图 / 已选资产
// ============================================================

export const getAssetKey = (type, asset) => `${type}:${asset?.id}`;

const getStableAssetId = (type, asset) => {
  const prefix = type === "characters" ? "c" : type === "scenes" ? "s" : "p";
  return `${prefix}-${asset?.id}`;
};

const parseAssetAliases = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => firstText(item)).filter(Boolean);
  }
  return firstText(value)
    .split(/[、,，/|｜;\s]+/)
    .map((item) => item.trim())
    .filter(Boolean);
};

export const buildNamedReference = (type, asset) => {
  const imageUrl = getAssetImageUrl(asset);
  if (!imageUrl) return null;
  const typeLabel = type === "characters" ? "人物" : type === "scenes" ? "场景" : "道具";
  const guaVideoMeta = getAssetGuaVideoMeta(asset, imageUrl);
  const assetName = firstText(asset?.name, asset?.title, asset?.characterName, asset?.sceneName, asset?.propName, "未命名");
  return {
    id: getAssetKey(type, asset),
    assetId: getStableAssetId(type, asset),
    name: `${typeLabel}：${assetName}`,
    base64: imageUrl,
    sourceType: type,
    sourceAssetId: asset?.id != null ? String(asset.id) : "",
    aliases: parseAssetAliases(asset?.aliases ?? asset?.alias ?? asset?.nicknames),
    description: firstText(
      asset?.description,
      asset?.appearancePrompt,
      asset?.appearance_prompt,
      asset?.prompt,
      asset?.visualPrompt,
      asset?.stylePrompt,
      asset?.costumePrompt,
      asset?.background,
    ),
    ...guaVideoMeta,
    ...(type === "characters" ? {
      voiceSampleUrl: firstText(asset?.voiceSampleUrl),
      voiceSampleName: firstText(asset?.voiceSampleName),
      voiceBoundAt: asset?.voiceBoundAt ?? null,
    } : {}),
  };
};

export const buildSelectedRefsFromKeys = (assetGroups = {}, selectedKeys = new Set()) => {
  const grouped = { characters: [], scenes: [], props: [] };
  Object.entries(assetGroups || {}).forEach(([type, list]) => {
    grouped[type] = (list || [])
      .filter((asset) => selectedKeys.has(getAssetKey(type, asset)))
      .map((asset) => buildNamedReference(type, asset))
      .filter(Boolean);
  });
  return grouped;
};

export const summarizeSelectedRefsForLog = (selectedRefs = {}) => ({
  characters: (selectedRefs.characters || []).map((ref) => ({
    id: ref.id,
    assetId: ref.assetId,
    name: ref.name,
    hasImage: Boolean(firstText(ref.base64)),
    sourceAssetId: ref.sourceAssetId,
    libraryId: firstText(ref.libraryId, ref.assetLibraryId, ref.guaAssetId),
  })),
  scenes: (selectedRefs.scenes || []).map((ref) => ({
    id: ref.id,
    assetId: ref.assetId,
    name: ref.name,
    hasImage: Boolean(firstText(ref.base64)),
    sourceAssetId: ref.sourceAssetId,
    libraryId: firstText(ref.libraryId, ref.assetLibraryId, ref.guaAssetId),
  })),
  props: (selectedRefs.props || []).map((ref) => ({
    id: ref.id,
    assetId: ref.assetId,
    name: ref.name,
    hasImage: Boolean(firstText(ref.base64)),
    sourceAssetId: ref.sourceAssetId,
    libraryId: firstText(ref.libraryId, ref.assetLibraryId, ref.guaAssetId),
  })),
});

// ============================================================
// 故事板占位 / 合并
// ============================================================

export const createPlaceholderBoards = (count = BOARD_COUNT, duration = BOARD_SECONDS) => (
  Array.from({ length: count }, (_, index) => ({
    index: index + 1,
    title: `第${index + 1}张故事板`,
    durationSeconds: duration,
    timeRange: `${index * duration}s-${(index + 1) * duration}s`,
    segmentText: "",
    imageUrl: "",
    prompt: "",
    status: "pending",
  }))
);

export const mergeBoardIntoList = (current, incoming) => {
  const index = Number(incoming?.index) || 1;
  const count = Math.max(current.length, index);
  const next = current.length ? [...current] : createPlaceholderBoards(count);
  while (next.length < count) {
    const nextIndex = next.length + 1;
    next.push({
      index: nextIndex,
      title: `第${nextIndex}张故事板`,
      durationSeconds: BOARD_SECONDS,
      timeRange: `${(nextIndex - 1) * BOARD_SECONDS}s-${nextIndex * BOARD_SECONDS}s`,
      status: "pending",
    });
  }
  next[index - 1] = {
    ...next[index - 1],
    ...incoming,
    status: incoming?.status || (incoming?.imageUrl ? "success" : "failed"),
  };
  return next;
};

// ============================================================
// 剧本单元 / 手动拆板
// ============================================================

const NON_SPOKEN_LABELS = new Set([
  "场景", "人物", "画面", "动作", "音效", "声音", "镜头", "景别", "时长",
  "地点", "时间", "转场", "情绪", "悬念", "反转点", "视觉", "氛围",
  "字幕", "切黑字幕", "屏幕文字", "上屏文字", "系统提示",
  "本集时长", "本集核心爽点", "中段冲突", "本集信息增量", "本集情绪曲线",
]);

// 分段类型徽标色：主题变量；colorSoft 为 13% 底（替代旧的 `${color}20` hex alpha 拼接）
export const SCRIPT_UNIT_TYPE_META = {
  visual: { label: "画面", color: "var(--color-blue-primary)", colorSoft: "color-mix(in srgb, var(--color-blue-primary) 13%, transparent)" },
  dialogue: { label: "对白", color: "var(--color-success)", colorSoft: "color-mix(in srgb, var(--color-success) 13%, transparent)" },
  os: { label: "内心OS", color: "var(--color-purple-primary)", colorSoft: "color-mix(in srgb, var(--color-purple-primary) 13%, transparent)" },
  sound: { label: "音效", color: "var(--color-warning)", colorSoft: "color-mix(in srgb, var(--color-warning) 13%, transparent)" },
  note: { label: "说明", color: "var(--color-text-muted)", colorSoft: "color-mix(in srgb, var(--color-text-muted) 13%, transparent)" },
};

const sentenceTerminators = new Set(["。", "！", "？", "!", "?"]);
const closingPunctuation = new Set(["”", "’", "\"", "'", "）", ")", "】", "]", "》", ">", "」", "』"]);

const firstColonIndexInText = (value) => {
  const cn = value.indexOf("：");
  const en = value.indexOf(":");
  if (cn < 0) return en;
  if (en < 0) return cn;
  return Math.min(cn, en);
};

const splitLineIntoCompleteUnits = (line) => {
  const value = firstText(line);
  if (!value) return [];
  const colonIndex = firstColonIndexInText(value);
  const label = colonIndex > 0 && colonIndex <= 20 ? value.slice(0, colonIndex).trim() : "";
  if (label && !NON_SPOKEN_LABELS.has(label)) {
    return [value];
  }

  const units = [];
  let current = "";
  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i];
    current += ch;
    const isEllipsis = ch === "…" && value[i + 1] === "…";
    if (isEllipsis) {
      i += 1;
      current += value[i];
    }
    if (sentenceTerminators.has(ch) || isEllipsis) {
      while (i + 1 < value.length && closingPunctuation.has(value[i + 1])) {
        i += 1;
        current += value[i];
      }
      if (firstText(current)) units.push(firstText(current));
      current = "";
    }
  }
  if (firstText(current)) units.push(firstText(current));
  return units.length ? units : [value];
};

const classifyScriptUnit = (rawText, index) => {
  const raw = firstText(rawText);
  const withoutMarker = raw.replace(/^[△▲]\s*/, "").trim();
  const colonIndex = firstColonIndexInText(withoutMarker);
  const label = colonIndex > 0 && colonIndex <= 20 ? withoutMarker.slice(0, colonIndex).trim() : "";
  const content = label ? withoutMarker.slice(colonIndex + 1).trim() : withoutMarker;
  const lowerLabel = label.toLowerCase();
  let type = "note";
  let speaker = "";

  if (/^[△▲]/.test(raw) || ["画面", "动作", "镜头", "场景", "转场", "景别", "地点", "时间"].includes(label)) {
    type = "visual";
  } else if (["音效", "声音", "环境声"].includes(label)) {
    type = "sound";
  } else if (label && (lowerLabel.includes("os") || lowerLabel.includes("vo") || label.includes("内心") || label.includes("心声") || label.includes("旁白") || label.includes("画外音"))) {
    type = "os";
    speaker = label;
  } else if (label && !NON_SPOKEN_LABELS.has(label)) {
    type = "dialogue";
    speaker = label;
  } else if (/^(OS|os|旁白|内心|心声|画外音)[：:]/.test(raw)) {
    type = "os";
  }

  const spokenText = ["dialogue", "os"].includes(type) ? firstText(content, raw) : "";
  return {
    id: `unit-${index + 1}`,
    type,
    typeLabel: SCRIPT_UNIT_TYPE_META[type]?.label || "说明",
    speaker,
    text: firstText(content, raw),
    rawText: raw,
    spokenText,
    spokenLength: spokenText.replace(/\s+/g, "").length,
  };
};

export const parseScriptUnits = (script) => {
  const lines = firstText(script).replace(/\r\n?/g, "\n").split(/\n+/);
  const units = [];
  lines.forEach((line) => {
    const isVisualLine = /^[△▲]/.test(firstText(line));
    splitLineIntoCompleteUnits(line).forEach((unit) => {
      const typedUnit = isVisualLine && !/^[△▲]/.test(unit) ? `△ ${unit}` : unit;
      units.push(classifyScriptUnit(typedUnit, units.length));
    });
  });
  return units;
};

const summarizeSegmentUnits = (units) => ({
  spokenCharCount: units.reduce((sum, unit) => sum + (Number(unit.spokenLength) || 0), 0),
  visualCount: units.filter((unit) => unit.type === "visual").length,
  dialogueCount: units.filter((unit) => unit.type === "dialogue").length,
  osCount: units.filter((unit) => unit.type === "os").length,
  soundCount: units.filter((unit) => unit.type === "sound").length,
});

const buildNarrationUnitText = (unit) => {
  const raw = firstText(unit?.rawText, unit?.text);
  if (!raw) return "";
  const withoutMarker = raw.replace(/^[△▲]\s*/, "").trim();
  const colonIndex = firstColonIndexInText(withoutMarker);
  const label = colonIndex > 0 && colonIndex <= 20 ? withoutMarker.slice(0, colonIndex).trim() : "";
  const content = label ? withoutMarker.slice(colonIndex + 1).trim() : withoutMarker;
  if (["人物", "音效", "声音", "环境声", "字幕", "切黑字幕", "屏幕文字", "上屏文字", "系统提示"].includes(label)) return "";
  if (["场景", "画面", "动作", "镜头", "地点", "时间", "转场", "景别", "视觉", "氛围"].includes(label)) return content;
  return content || withoutMarker;
};

export const normalizeManualSegments = (segments = [], duration = BOARD_SECONDS) => (
  (segments || [])
    .map((segment, index) => {
      const beats = (Array.isArray(segment?.beats) ? segment.beats : []).map((beat, beatIndex) => ({
        ...beat,
        id: beat.id || `unit-${index + 1}-${beatIndex + 1}`,
      }));
      const stats = summarizeSegmentUnits(beats);
      const sourceText = beats.map((beat) => beat.rawText || beat.text).filter(Boolean).join("\n");
      const spokenText = beats.map((beat) => beat.spokenText).filter(Boolean).join(" ");
      return {
        ...segment,
        index: index + 1,
        title: firstText(segment.title, `第${index + 1}张故事板`),
        durationSeconds: duration,
        timeRange: `${index * duration}s-${(index + 1) * duration}s`,
        sourceText: firstText(sourceText, segment.sourceText),
        spokenText: firstText(spokenText, segment.spokenText),
        beats,
        ...stats,
        warning: stats.spokenCharCount > 40 ? `对白/旁白/OS 约 ${stats.spokenCharCount} 字，可能需要更慢节奏或继续拆分。` : "",
      };
    })
);

export const buildManualStoryboardSegments = (script, minimumCount = BOARD_COUNT, duration = BOARD_SECONDS, presentationMode = "formal") => {
  const mode = normalizePresentationMode(presentationMode);
  const units = parseScriptUnits(script).map((unit) => {
    if (mode !== "narration") return unit;
    const narrationText = buildNarrationUnitText(unit);
    return {
      ...unit,
      spokenText: narrationText,
      spokenLength: narrationText.replace(/\s+/g, "").length,
    };
  });
  if (!units.length) return [];
  const totalSpoken = units.reduce((sum, unit) => sum + (Number(unit.spokenLength) || 0), 0);
  // 40 字只约束对白、旁白、OS 等要说出口的内容；画面描述只按较宽松的覆盖密度拆分。
  const visualCoverageCount = Math.ceil(units.length / 10);
  const desiredCount = Math.max(
    minimumCount,
    Math.ceil(totalSpoken / 40),
    visualCoverageCount
  );
  const count = Math.min(24, Math.max(minimumCount, desiredCount));
  const segments = Array.from({ length: count }, (_, index) => ({
    index: index + 1,
    title: `第${index + 1}张故事板`,
    beats: [],
  }));

  let cursor = 0;
  units.forEach((unit, unitIndex) => {
    const remainingUnits = units.length - unitIndex;
    const remainingSlots = count - cursor;
    const current = segments[cursor];
    const currentStats = summarizeSegmentUnits(current.beats);
    const shouldAdvance = current.beats.length > 0
      && cursor < count - 1
      && (
        currentStats.spokenCharCount + (unit.spokenLength || 0) > 40
        || (current.beats.length >= 10 && remainingUnits > remainingSlots)
      );
    if (shouldAdvance) cursor += 1;
    segments[cursor].beats.push(unit);
  });

  return normalizeManualSegments(segments, duration);
};

// ============================================================
// 视频输入包 / 参考图打包
// ============================================================

export const flattenSelectedRefs = (selectedRefs) => [
  ...(selectedRefs?.characters || []),
  ...(selectedRefs?.scenes || []),
  ...(selectedRefs?.props || []),
].filter((item) => firstText(item?.base64));

export const createVideoPackageId = (item) => [
  item?.role || "reference",
  item?.type || "参考图",
  item?.name || "未命名",
  normalizeImageUrl(item?.url),
].join(":");

export const getAssetMetaForUrl = (assetMetaMap = {}, url = "") => {
  const rawUrl = firstText(url);
  const normalizedUrl = normalizeImageUrl(rawUrl);
  return assetMetaMap[rawUrl] || assetMetaMap[normalizedUrl] || null;
};

export const getPackageItemGuaMeta = (item, assetMetaMap = {}, preferredProvider = "") => {
  const mapped = getAssetMetaForUrl(assetMetaMap, item?.url);
  const meta = {
    guaVideoAssetId: firstText(mapped?.guaVideoAssetId, item?.guaVideoAssetId),
    guaVideoAssetStatus: firstText(mapped?.guaVideoAssetStatus, item?.guaVideoAssetStatus),
    engGuaVideoAssetId: firstText(mapped?.engGuaVideoAssetId, item?.engGuaVideoAssetId),
    engGuaVideoAssetStatus: firstText(mapped?.engGuaVideoAssetStatus, item?.engGuaVideoAssetStatus),
    guaOminiAssetId: firstText(mapped?.guaOminiAssetId, item?.guaOminiAssetId),
    guaOminiAssetStatus: firstText(mapped?.guaOminiAssetStatus, item?.guaOminiAssetStatus),
    dreaminaProxyAssetId: firstText(mapped?.dreaminaProxyAssetId, item?.dreaminaProxyAssetId),
    dreaminaProxyAssetStatus: firstText(mapped?.dreaminaProxyAssetStatus, item?.dreaminaProxyAssetStatus),
    provider: firstText(mapped?.provider, item?.assetProvider, item?.provider),
  };
  const effective = getEffectiveAssetMeta(meta, preferredProvider);
  return {
    ...meta,
    assetId: effective.assetId,
    status: effective.status,
    effectiveProvider: effective.provider,
  };
};

export const sanitizeRefMarkerText = (value) => firstText(value).replace(/[:}]/g, "：");

export const createPromptReferenceMarker = (item, index) => {
  const category = sanitizeRefMarkerText(item?.type || item?.role || "参考图");
  const label = sanitizeRefMarkerText(`图${index + 1} ${item?.name || item?.type || "参考图"}`);
  return `{{ref:${category}:${label}:${normalizeImageUrl(item?.url)}}}`;
};

export const hydratePromptReferenceChips = (text, items = []) => {
  const refs = normalizeVideoPackageItems(items);
  if (!text || !refs.length) return text || "";
  return text
    .replace(/@(?:图片|图)(\d+)/g, (match, rawIndex) => {
      const ref = refs[Number(rawIndex) - 1];
      return ref ? createPromptReferenceMarker(ref, Number(rawIndex) - 1) : match;
    })
    .replace(/参考图(\d+)/g, (match, rawIndex) => {
      const ref = refs[Number(rawIndex) - 1];
      return ref ? createPromptReferenceMarker(ref, Number(rawIndex) - 1) : match;
    });
};

export const normalizeVideoPackageItems = (items, limit = VIDEO_REF_LIMIT) => {
  const normalizedLimit = Math.max(1, Number(limit) || VIDEO_REF_LIMIT);
  const seen = new Set();
  return (items || [])
    .map((item) => ({
      id: firstText(item?.id) || createVideoPackageId(item),
      role: firstText(item?.role, `reference_${getSeedance25ReferenceMediaType(item)}`),
      type: firstText(item?.type, "参考图"),
      mediaType: getSeedance25ReferenceMediaType(item),
      name: firstText(item?.name, "参考图"),
      url: normalizeImageUrl(getSeedance25ReferenceUrl(item)),
      note: firstText(item?.note),
      guaVideoAssetId: firstText(item?.guaVideoAssetId),
      guaVideoAssetStatus: firstText(item?.guaVideoAssetStatus),
      engGuaVideoAssetId: firstText(item?.engGuaVideoAssetId),
      engGuaVideoAssetStatus: firstText(item?.engGuaVideoAssetStatus),
      guaOminiAssetId: firstText(item?.guaOminiAssetId),
      guaOminiAssetStatus: firstText(item?.guaOminiAssetStatus),
      dreaminaProxyAssetId: firstText(item?.dreaminaProxyAssetId),
      dreaminaProxyAssetStatus: firstText(item?.dreaminaProxyAssetStatus),
      assetProvider: firstText(item?.assetProvider, item?.provider),
      voiceSampleUrl: firstText(item?.voiceSampleUrl),
      voiceSampleName: firstText(item?.voiceSampleName),
      voiceBoundAt: item?.voiceBoundAt ?? null,
    }))
    .filter((item) => {
      if (!item.url || seen.has(item.url)) return false;
      seen.add(item.url);
      return true;
    })
    .slice(0, normalizedLimit);
};

export const buildAssetVideoPackageItems = (selectedRefs, limit = VIDEO_REF_LIMIT) => normalizeVideoPackageItems(
  flattenSelectedRefs(selectedRefs).map((ref) => ({
    id: createVideoPackageId({ role: "asset_reference", name: ref.name, url: ref.base64 }),
    role: "asset_reference",
    type: firstText(ref.name).split("：")[0] || "参考图",
    name: ref.name || "参考图",
    url: ref.base64,
    guaVideoAssetId: ref.guaVideoAssetId,
    guaVideoAssetStatus: ref.guaVideoAssetStatus,
    engGuaVideoAssetId: ref.engGuaVideoAssetId,
    engGuaVideoAssetStatus: ref.engGuaVideoAssetStatus,
    guaOminiAssetId: ref.guaOminiAssetId,
    guaOminiAssetStatus: ref.guaOminiAssetStatus,
    dreaminaProxyAssetId: ref.dreaminaProxyAssetId,
    dreaminaProxyAssetStatus: ref.dreaminaProxyAssetStatus,
    assetProvider: ref.assetProvider || ref.provider,
    voiceSampleUrl: ref.voiceSampleUrl,
    voiceSampleName: ref.voiceSampleName,
    voiceBoundAt: ref.voiceBoundAt,
  })),
  limit,
);

const normalizeRefText = (value) => firstText(value)
  .replace(/^(人物|角色|场景|地点|道具|物件)[:：]/, "")
  .replace(/[^\p{L}\p{N}]+/gu, " ")
  .toLowerCase()
  .trim();

const splitRefNameText = (value) => firstText(value)
  .split(/[、,，;；/\n]+/)
  .map((item) => firstText(item))
  .filter((item) => item && !["无", "暂无", "已附参考图"].includes(item));

const stringListFromUnknown = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      if (typeof item === "string") return splitRefNameText(item);
      return splitRefNameText(firstText(item?.name, item?.title, item?.label));
    });
  }
  return splitRefNameText(value);
};

const parseBoardRefNamesFromPrompt = (prompt, label) => {
  const text = firstText(prompt);
  if (!text) return [];
  const names = [];
  const currentSection = text.match(new RegExp(`# 当前页必须优先使用的参考图[\\s\\S]*?${label}[:：]([^\\n]+)`));
  names.push(...splitRefNameText(currentSection?.[1]));
  const legendRegex = new RegExp(`Image\\s+[A-Z]\\s*=\\s*${label}[:：]([^\\n]+)`, "g");
  let match;
  while ((match = legendRegex.exec(text))) {
    names.push(...splitRefNameText(match[1]));
  }
  return names;
};

export const extractBoardReferenceNames = (board, group) => {
  const referenceNames = board?.referenceNames || {};
  const referenceCounts = board?.referenceCounts || {};
  const keyMap = {
    characters: ["characterRefs", "characters", "characterNames"],
    scenes: ["sceneRefs", "scenes", "sceneNames"],
    props: ["propRefs", "props", "propNames"],
  };
  const labelMap = { characters: "人物", scenes: "场景", props: "道具" };
  const names = [];
  (keyMap[group] || []).forEach((key) => {
    names.push(...stringListFromUnknown(board?.[key]));
    names.push(...stringListFromUnknown(referenceNames?.[key]));
    names.push(...stringListFromUnknown(referenceCounts?.[key]));
  });
  names.push(...parseBoardRefNamesFromPrompt(board?.prompt, labelMap[group]));
  const seen = new Set();
  return names.filter((name) => {
    const normalized = normalizeRefText(name);
    if (!normalized || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

const refMatchesBoard = (ref, requiredNames, planText) => {
  const refName = normalizeRefText(ref?.name);
  if (!refName) return false;
  const normalizedRequired = (requiredNames || []).map(normalizeRefText).filter(Boolean);
  if (normalizedRequired.some((name) => refName.includes(name) || name.includes(refName))) return true;
  const normalizedPlan = normalizeRefText(planText);
  if (!normalizedPlan) return false;
  if (normalizedPlan.includes(refName) || refName.includes(normalizedPlan)) return true;
  return refName.split(/\s+/).some((token) => token.length >= 2 && normalizedPlan.includes(token));
};

const selectBoardRefs = (refs, requiredNames, planText, fallbackAll = false) => {
  const selected = (refs || []).filter((ref) => refMatchesBoard(ref, requiredNames, planText));
  if (selected.length) return selected;
  return fallbackAll ? (refs || []) : [];
};

export const buildBoardAssetVideoPackageItems = (board, selectedRefs, limit = VIDEO_REF_LIMIT) => {
  const planText = [
    board?.title,
    board?.narrativeFunction,
    board?.rhythmKeyword,
    board?.sourceText,
    board?.segmentText,
    board?.spokenText,
  ].map(firstText).filter(Boolean).join("\n");
  const characters = selectBoardRefs(
    selectedRefs?.characters || [],
    extractBoardReferenceNames(board, "characters"),
    planText,
    false
  );
  const scenes = selectBoardRefs(
    selectedRefs?.scenes || [],
    extractBoardReferenceNames(board, "scenes"),
    planText,
    true
  );
  const props = selectBoardRefs(
    selectedRefs?.props || [],
    extractBoardReferenceNames(board, "props"),
    planText,
    false
  );
  return buildAssetVideoPackageItems({ characters, scenes, props }, limit);
};

export const enrichVideoPackageItems = (items, candidateItems = [], assetMetaMap = {}, limit = VIDEO_REF_LIMIT) => {
  const metaByUrl = new Map();
  normalizeVideoPackageItems(candidateItems, limit).forEach((candidate) => {
    const meta = getPackageItemGuaMeta(candidate, assetMetaMap);
    if (candidate.url && (meta.guaVideoAssetId || meta.engGuaVideoAssetId || meta.guaOminiAssetId || meta.dreaminaProxyAssetId)) {
      metaByUrl.set(candidate.url, {
        guaVideoAssetId: meta.guaVideoAssetId,
        guaVideoAssetStatus: meta.guaVideoAssetStatus,
        engGuaVideoAssetId: meta.engGuaVideoAssetId,
        engGuaVideoAssetStatus: meta.engGuaVideoAssetStatus,
        guaOminiAssetId: meta.guaOminiAssetId,
        guaOminiAssetStatus: meta.guaOminiAssetStatus,
        dreaminaProxyAssetId: meta.dreaminaProxyAssetId,
        dreaminaProxyAssetStatus: meta.dreaminaProxyAssetStatus,
        assetProvider: meta.provider,
      });
    }
  });
  return normalizeVideoPackageItems(items, limit).map((item) => {
    const meta = metaByUrl.get(item.url) || getPackageItemGuaMeta(item, assetMetaMap);
    if (
      !meta?.guaVideoAssetId
      && !meta?.guaVideoAssetStatus
      && !meta?.engGuaVideoAssetId
      && !meta?.engGuaVideoAssetStatus
      && !meta?.guaOminiAssetId
      && !meta?.guaOminiAssetStatus
      && !meta?.dreaminaProxyAssetId
      && !meta?.dreaminaProxyAssetStatus
    ) return item;
    return { ...item, ...meta };
  });
};

const mergeAssetField = (item, meta, field) => (
  Object.prototype.hasOwnProperty.call(meta, field)
    ? firstText(meta?.[field])
    : firstText(item?.[field])
);

export const applyGuaMetaToPackageItems = (items, targetUrl, meta = {}) => {
  const normalizedTarget = normalizeImageUrl(targetUrl);
  if (!normalizedTarget) return items || [];
  return normalizeVideoPackageItems(items).map((item) => {
    if (normalizeImageUrl(item.url) !== normalizedTarget) return item;
    return {
      ...item,
      guaVideoAssetId: mergeAssetField(item, meta, "guaVideoAssetId"),
      guaVideoAssetStatus: mergeAssetField(item, meta, "guaVideoAssetStatus"),
      engGuaVideoAssetId: mergeAssetField(item, meta, "engGuaVideoAssetId"),
      engGuaVideoAssetStatus: mergeAssetField(item, meta, "engGuaVideoAssetStatus"),
      guaOminiAssetId: mergeAssetField(item, meta, "guaOminiAssetId"),
      guaOminiAssetStatus: mergeAssetField(item, meta, "guaOminiAssetStatus"),
      dreaminaProxyAssetId: mergeAssetField(item, meta, "dreaminaProxyAssetId"),
      dreaminaProxyAssetStatus: mergeAssetField(item, meta, "dreaminaProxyAssetStatus"),
      assetProvider: firstText(meta.provider, meta.assetProvider, item.assetProvider),
    };
  });
};

export const splitPackageAssetRefs = (items) => {
  const grouped = { characters: [], scenes: [], props: [] };
  normalizeVideoPackageItems(items).forEach((item) => {
    const ref = {
      name: item.name,
      base64: item.url,
      guaVideoAssetId: item.guaVideoAssetId,
      guaVideoAssetStatus: item.guaVideoAssetStatus,
      engGuaVideoAssetId: item.engGuaVideoAssetId,
      engGuaVideoAssetStatus: item.engGuaVideoAssetStatus,
      guaOminiAssetId: item.guaOminiAssetId,
      guaOminiAssetStatus: item.guaOminiAssetStatus,
      dreaminaProxyAssetId: item.dreaminaProxyAssetId,
      dreaminaProxyAssetStatus: item.dreaminaProxyAssetStatus,
      assetProvider: item.assetProvider,
    };
    if (item.type === "人物" || item.name.startsWith("人物：")) grouped.characters.push(ref);
    if (item.type === "场景" || item.name.startsWith("场景：")) grouped.scenes.push(ref);
    if (item.type === "道具" || item.name.startsWith("道具：")) grouped.props.push(ref);
  });
  return grouped;
};

// ============================================================
// 故事板前后关联
// ============================================================

export const findPreviousSuccessBoard = (boards, boardIndex) => {
  const index = Number(boardIndex);
  if (!index || !Array.isArray(boards)) return null;
  return [...boards]
    .filter((item) => Number(item?.index) < index && normalizeImageUrl(item?.imageUrl))
    .sort((a, b) => Number(b?.index) - Number(a?.index))[0] || null;
};

export const buildPreviousBoardSummary = (board, boards) => {
  const previous = findPreviousSuccessBoard(boards, board?.index);
  if (!previous) return "";
  const previousScript = cleanScriptSegmentForPrompt(firstText(previous.sourceText, previous.segmentText));
  const previousEmotion = formatBoardEmotionPlan(previous);
  const previousBlocking = formatBoardBlockingPlan(previous);
  return [
    firstText(
      previousScript,
      previous.title ? `来自${previous.title}，用于保持连戏。` : "",
    ),
    previousEmotion ? `上一段情绪状态：\n${previousEmotion}` : "",
    previousBlocking ? `上一段空间站位：\n${previousBlocking}` : "",
  ].filter(Boolean).join("\n");
};

export const buildDefaultVideoPackageItems = (board, boards, selectedRefs = {}, limit = VIDEO_REF_LIMIT) => {
  const previous = findPreviousSuccessBoard(boards, board?.index);
  const boardAssetRefs = buildBoardAssetVideoPackageItems(board, selectedRefs, limit)
    .slice(0, Math.max(0, limit - (previous ? 2 : 1)));
  return normalizeVideoPackageItems([
    {
      id: `board:${board?.index || "current"}`,
      role: "current_board",
      type: "当前故事板",
      name: `当前故事板 ${board?.index || ""}`.trim(),
      url: board?.imageUrl,
      note: "视频主参考",
    },
    ...boardAssetRefs,
    previous ? {
      id: `previous-board:${previous?.index}`,
      role: "previous_board",
      type: "前一张故事板",
      name: `前一张故事板 ${previous?.index}`,
      url: previous?.imageUrl,
      note: "连戏参考",
    } : null,
  ].filter(Boolean), limit);
};

// ============================================================
// 视频草稿初始化 / 提示词构建
// ============================================================

export const ensureVideoDraft = (board, boards, selectedRefs, draft = {}, aspectRatio = DEFAULT_VIDEO_RATIO, assetMetaMap = {}, limit = VIDEO_REF_LIMIT) => {
  const basePackage = Array.isArray(draft?.inputPackage)
    ? draft.inputPackage
    : buildDefaultVideoPackageItems(board, boards, selectedRefs, limit);
  const draftPrompt = firstText(draft?.prompt);
  const isStaleDefaultPrompt = draftPrompt.startsWith("请根据视频输入包生成")
    || draftPrompt.includes("图片绑定顺序：")
    || draftPrompt.includes("本张故事板：")
    || draftPrompt.includes("本张视频输入包说明：")
    || draftPrompt.includes("优先级规则：")
    || draftPrompt.includes("中国大陆过审安全规则")
    || draftPrompt.includes("当前视频只生成以下剧本原文")
    || draftPrompt.includes("当前视频只改编以下剧本原文");
  return {
    ratio: firstText(draft?.ratio, aspectRatio, DEFAULT_VIDEO_RATIO),
    duration: normalizeVideoDuration(draft?.duration ?? board?.durationSeconds ?? BOARD_SECONDS),
    resolution: firstText(draft?.resolution, GUA_VIDEO_RESOLUTION),
    generateAudio: draft?.generateAudio ?? true,
    noSubtitle: draft?.noSubtitle ?? true,
    lighting: firstText(draft?.lighting, DEFAULT_VIDEO_LIGHTING),
    previousSummary: draft?.previousSummary ?? buildPreviousBoardSummary(board, boards),
    inputPackage: enrichVideoPackageItems(basePackage, buildAssetVideoPackageItems(selectedRefs, limit), assetMetaMap, limit),
    prompt: isStaleDefaultPrompt ? "" : draftPrompt,
  };
};

export const describeVideoPackageItems = (items, limit = VIDEO_REF_LIMIT) => {
  const refs = normalizeVideoPackageItems(items, limit);
  if (!refs.length) return "没有图片参考";
  return refs.map((ref, index) => `${createPromptReferenceMarker(ref, index)}：${ref.name || ref.type || "参考图"}（${ref.type || ref.role || "参考"}）`).join("\n");
};

export const cleanScriptSegmentForPrompt = (text) => firstText(text)
  .replace(/可朗读\/可上屏（[^）]*）：/g, "")
  .replace(/可朗读\/可上屏/g, "")
  .split(/\r?\n/)
  .filter((line) => !/^本页必须完整(念完|执行)的(台词|对白|旁白|字幕|系统提示)/.test(firstText(line)))
  .join("\n")
  .trim();

const isSceneNoteLineForPrompt = (line) => {
  const value = firstText(line);
  if (!value) return false;
  if (/^\d+\s*[-－]\s*\d+/.test(value)) return true;
  if (/^第\s*\d+\s*(场|幕|段|集)/.test(value)) return true;
  if (/^(日|夜|晨|午|下午|傍晚|黄昏|清晨|凌晨)\s+(内|外)/.test(value)) return true;
  if (/^(内|外)\s+/.test(value)) return true;
  const colonIndex = firstColonIndexInText(value);
  const label = colonIndex > 0 && colonIndex <= 20 ? value.slice(0, colonIndex).trim() : "";
  return ["场景", "人物", "地点", "时间", "音效", "声音", "环境声"].includes(label);
};

export const collectSpokenTextForVideoPrompt = (text) => {
  const segment = cleanScriptSegmentForPrompt(text);
  if (!segment) return "";
  const lines = segment.replace(/\r\n?/g, "\n").split(/\n+/).map(firstText).filter(Boolean);
  const blocks = [];
  let current = "";

  const flush = () => {
    if (current) blocks.push(current.trim());
    current = "";
  };

  lines.forEach((line) => {
    if (/^[△▲]/.test(line)) {
      flush();
      return;
    }
    if (isSceneNoteLineForPrompt(line)) {
      flush();
      return;
    }
    const withoutMarker = line.replace(/^[△▲]\s*/, "").trim();
    const colonIndex = firstColonIndexInText(withoutMarker);
    const label = colonIndex > 0 && colonIndex <= 20 ? withoutMarker.slice(0, colonIndex).trim() : "";
    const lowerLabel = label.toLowerCase();
    const isVisualLabel = ["画面", "动作", "镜头", "场景", "转场", "景别", "地点", "时间", "音效", "声音", "环境声"].includes(label);
    const isSpokenLabel = label && (
      lowerLabel.includes("os")
      || lowerLabel.includes("vo")
      || label.includes("内心")
      || label.includes("心声")
      || label.includes("旁白")
      || label.includes("画外音")
      || !NON_SPOKEN_LABELS.has(label)
    );

    if (isVisualLabel) {
      flush();
      return;
    }

    if (isSpokenLabel) {
      flush();
      current = line;
      return;
    }

    if (current) {
      current = `${current}\n${line}`;
    } else if (line.includes("“") && line.includes("”")) {
      blocks.push(line);
    }
  });

  flush();
  return blocks.join("\n").trim();
};

const buildNarrationTextForVideoPrompt = (text) => {
  const units = parseScriptUnits(cleanScriptSegmentForPrompt(text));
  return units.map(buildNarrationUnitText).filter(Boolean).join("\n").trim();
};

const collectVisualDirectionsForVideoPrompt = (text) => {
  const segment = cleanScriptSegmentForPrompt(text);
  if (!segment) return "";
  const lines = segment.replace(/\r\n?/g, "\n").split(/\n+/).map(firstText).filter(Boolean);
  const result = [];
  let inSpokenBlock = false;

  lines.forEach((line) => {
    const withoutMarker = line.replace(/^[△▲]\s*/, "").trim();
    const colonIndex = firstColonIndexInText(withoutMarker);
    const label = colonIndex > 0 && colonIndex <= 20 ? withoutMarker.slice(0, colonIndex).trim() : "";
    const lowerLabel = label.toLowerCase();
    const isVisual = /^[△▲]/.test(line)
      || ["画面", "动作", "镜头", "场景", "转场", "景别", "地点", "时间", "音效", "声音", "环境声", "人物"].includes(label)
      || isSceneNoteLineForPrompt(line);
    const isSpoken = label && (
      lowerLabel.includes("os")
      || lowerLabel.includes("vo")
      || label.includes("内心")
      || label.includes("心声")
      || label.includes("旁白")
      || label.includes("画外音")
      || !NON_SPOKEN_LABELS.has(label)
    );

    if (isVisual) {
      result.push(line);
      inSpokenBlock = false;
      return;
    }
    if (isSpoken) {
      inSpokenBlock = true;
      return;
    }
    if (!inSpokenBlock) result.push(line);
  });

  return result.join("\n").trim();
};

export const formatBoardEmotionPlan = (board) => {
  const plan = board?.emotionPlan || {};
  const emotionalArc = firstText(board?.emotionalArc, plan.emotionalArc);
  const emotionContinuity = firstText(board?.emotionContinuity, plan.emotionContinuity);
  const dialogueEmotions = Array.isArray(board?.dialogueEmotions)
    ? board.dialogueEmotions
    : (Array.isArray(plan.dialogueEmotions) ? plan.dialogueEmotions : []);
  const characterEmotions = Array.isArray(board?.characterEmotions)
    ? board.characterEmotions
    : (Array.isArray(plan.characterEmotions) ? plan.characterEmotions : []);
  const lines = [];
  if (emotionalArc) lines.push(`情绪弧线：${emotionalArc}`);
  if (emotionContinuity) lines.push(`情绪承接：${emotionContinuity}`);
  if (characterEmotions.length) {
    lines.push("人物情绪：");
    characterEmotions.slice(0, 6).forEach((item) => {
      const name = firstText(item?.character, item?.name, item?.speaker);
      const start = firstText(item?.start);
      const end = firstText(item?.end, item?.emotion);
      const change = item?.intensity != null ? String(item.intensity) : firstText(item?.intensityChange);
      const trigger = firstText(item?.trigger, item?.performance);
      lines.push(`- ${name || "人物"}：${[start, end].filter(Boolean).join("→") || "自然递进"}${change ? `｜强度${change}` : ""}${trigger ? `｜${trigger}` : ""}`);
    });
  }
  if (dialogueEmotions.length) {
    lines.push("逐句对白/旁白情绪：");
    dialogueEmotions.slice(0, 8).forEach((item) => {
      const speaker = firstText(item?.speaker, item?.character, "角色");
      const line = firstText(item?.line, item?.text);
      const emotion = firstText(item?.emotion);
      const intensity = item?.intensity != null ? String(item.intensity) : "";
      const performance = firstText(item?.performance);
      lines.push(`- ${speaker}${line ? `「${line}」` : ""}${emotion ? `｜${emotion}` : ""}${intensity ? `｜强度${intensity}` : ""}${performance ? `｜${performance}` : ""}`);
    });
  }
  return lines.join("\n").trim();
};

export const formatBoardBlockingPlan = (board) => {
  const plan = board?.blockingPlan || {};
  const sceneMap = firstText(board?.sceneMap, plan.sceneMap);
  const startBlocking = firstText(board?.startBlocking, plan.startBlocking);
  const movement = firstText(board?.movement, plan.movement);
  const endBlocking = firstText(board?.endBlocking, plan.endBlocking);
  const screenDirection = firstText(board?.screenDirection, plan.screenDirection);
  const cameraAxis = firstText(board?.cameraAxis, plan.cameraAxis);
  const focusOrder = Array.isArray(board?.focusOrder)
    ? board.focusOrder
    : (Array.isArray(plan.focusOrder) ? plan.focusOrder : []);
  const cutPlan = Array.isArray(board?.cutPlan)
    ? board.cutPlan
    : (Array.isArray(plan.cutPlan) ? plan.cutPlan : []);
  const lines = [];
  if (sceneMap) lines.push(`场景坐标：${sceneMap}`);
  if (startBlocking) lines.push(`开始站位：${startBlocking}`);
  if (movement) lines.push(`移动调度：${movement}`);
  if (endBlocking) lines.push(`结束站位：${endBlocking}`);
  if (screenDirection) lines.push(`画面关系：${screenDirection}`);
  if (cameraAxis) lines.push(`镜头轴线：${cameraAxis}`);
  if (focusOrder.length) lines.push(`焦点顺序：${focusOrder.map(firstText).filter(Boolean).join(" → ")}`);
  if (cutPlan.length) {
    lines.push("Cut镜头计划：");
    cutPlan.slice(0, 5).forEach((item, index) => {
      const cut = firstText(String(item?.cut ?? item?.index ?? index + 1));
      const time = firstText(item?.time, item?.timeRange);
      const shotSize = firstText(item?.shotSize, item?.["景别"]);
      const camera = firstText(item?.camera, item?.["镜头"]);
      const focus = firstText(item?.focus, item?.["焦点"]);
      const action = firstText(item?.action, item?.["动作"]);
      const blocking = firstText(item?.blocking, item?.["站位"]);
      lines.push(`- Cut ${cut}${time ? ` ${time}` : ""}${shotSize ? `｜${shotSize}` : ""}${camera ? `｜${camera}` : ""}${focus ? `｜焦点:${focus}` : ""}${action ? `｜${action}` : ""}${blocking ? `｜站位:${blocking}` : ""}`);
    });
  }
  return lines.join("\n").trim();
};

const VIDEO_PROMPT_REFERENCE_PRIORITY_RULES = [
  "@图1 是当前故事板，只参考镜头、站位、动作节奏、光影和情绪，不要把故事板排版、边框、标题、说明文字或页面字幕生成进视频。",
  "人物、场景、道具外观以本输入包里对应的最终参考图为准；故事板上的人物只用于识别角色，不覆盖最终参考图。",
  "如果故事板文字与本提示词冲突，以本提示词里的对白、VO、OS、旁白、系统音为准。",
].join("\n");

const MAINLAND_REVIEW_SAFETY_RULES = "平台合规表达：冲突、受伤、争议道具、机构或制服元素都用克制、非血腥、非低俗、虚构中性的短剧画面处理。";

const formatBoardShotPlanForVideoPrompt = (board) => {
  const cutPlan = Array.isArray(board?.cutPlan)
    ? board.cutPlan
    : (Array.isArray(board?.blockingPlan?.cutPlan) ? board.blockingPlan.cutPlan : []);
  if (cutPlan.length) {
    return cutPlan.slice(0, 5).map((item, index) => {
      const cut = firstText(String(item?.cut ?? item?.index ?? index + 1));
      const time = firstText(item?.time, item?.timeRange);
      const shotSize = firstText(item?.shotSize, item?.["景别"]);
      const camera = firstText(item?.camera, item?.["镜头"], item?.cameraMovement, item?.["机位/运动"]);
      const action = firstText(item?.action, item?.["动作"], item?.visual, item?.["画面"]);
      const sound = firstText(item?.sound, item?.["声音"], item?.sfx, item?.["音效"]);
      const text = firstText(item?.text, item?.dialogue, item?.["文本"], item?.spoken);
      return [
        `- 分镜${cut}${time ? `（${time}）` : ""}`,
        action ? `画面：${action}` : "",
        [shotSize, camera].filter(Boolean).length ? `机位/运动：${[shotSize, camera].filter(Boolean).join(" / ")}` : "",
        sound ? `声音：${sound}` : "",
        text ? `文本：${text}` : "文本：无",
      ].filter(Boolean).join("；");
    }).join("\n");
  }

  const visualSummary = firstText(board?.visualSummary, board?.narrativeFunction, board?.rhythmKeyword, board?.title);
  return [
    `- 分镜1（0-4秒）：建立场景、人物位置和本段情绪入口。${visualSummary ? `重点：${visualSummary}` : ""}`,
    "- 分镜2（4-10秒）：推进主要动作或对话冲突，镜头贴近人物反应。",
    "- 分镜3（10-15秒）：完成本段关键动作或完整台词，在自然可接下一段的位置收束。",
  ].join("\n");
};

export const buildBoardVideoPrompt = (board, episode, directorRequest, packageItems, previousSummary = "", presentationMode = "formal", referenceLimit = VIDEO_REF_LIMIT) => {
  const request = firstText(directorRequest);
  const refDescription = describeVideoPackageItems(packageItems, referenceLimit);
  const scriptSegment = cleanScriptSegmentForPrompt(firstText(board?.sourceText, board?.segmentText));
  const mode = normalizePresentationMode(board?.presentationMode || presentationMode);
  const exactSpokenText = collectSpokenTextForVideoPrompt(scriptSegment);
  const generatedNarrationText = buildNarrationTextForVideoPrompt(scriptSegment);
  const visualDirections = collectVisualDirectionsForVideoPrompt(scriptSegment);
  const narrationText = board?.presentationMode === "narration"
    ? firstText(board?.spokenText, generatedNarrationText)
    : firstText(generatedNarrationText, board?.spokenText);
  const previousContext = firstText(previousSummary);
  const emotionPlanText = formatBoardEmotionPlan(board);
  const blockingPlanText = formatBoardBlockingPlan(board);
  const shotPlanText = formatBoardShotPlanForVideoPrompt(board);
  const title = board?.title || `第${board?.index || ""}段`;
  const timeRange = board?.timeRange || "约15秒";
  const narrativeTask = firstText(board?.narrativeFunction, board?.rhythmKeyword, board?.visualSummary, title);
  const commonHeader = [
    "生成一段约15秒真人短剧视频。电影感、photorealistic、表演自然、镜头有张力。",
    "全程无字幕、无水印、不要出现故事板页面文字；不要生成静态拼贴。",
    episode?.title ? `当前集：${episode.title}` : "",
    `当前片段：${title}｜${timeRange}`,
    narrativeTask ? `本段戏剧任务：${narrativeTask}` : "",
    `参考图：\n${refDescription}`,
    mode === "blocking"
      ? "故事板参考图是黑白线稿模糊站位图：只读取构图、人物编号、站位、视线、移动路径、镜头轴线和景别节奏；不要把图中文字、编号、箭头生成成字幕或台词；人物真实外观以输入包角色参考图为准。"
      : "",
    mode === "grid9"
      ? "故事板参考图是黑白线稿无文字九宫格：只按9个画格读取镜头顺序、站位、视线、动线、景别和第9格收束状态；不要生成任何字幕、编号、图中文字或对白气泡；人物真实外观以输入包角色参考图为准。"
      : "",
    VIDEO_PROMPT_REFERENCE_PRIORITY_RULES,
    MAINLAND_REVIEW_SAFETY_RULES,
  ].filter(Boolean).join("\n");

  const compactContinuity = previousContext
    ? `上一段连戏参考（只看情绪余波、站位、动作方向和光影，不重复上一段剧情/台词）：\n“${previousContext}”`
    : "";
  const compactEmotion = emotionPlanText ? `情绪：\n${emotionPlanText}` : "";
  const compactBlocking = blockingPlanText ? `站位/调度：\n${blockingPlanText}` : "";

  if (mode === "narration") {
    return [
      commonHeader,
      compactContinuity,
      compactEmotion,
      compactBlocking,
      visualDirections ? `画面/动作依据（转成镜头和表演，不要显示成文字）：\n${visualDirections}` : "",
      narrationText ? `最终口播文本（旁白为主，少量对白按原文）：\n${narrationText}` : "本段没有口播文本，不要新增解释性旁白。",
      `分镜执行：\n${shotPlanText}`,
      "解说剧规则：旁白可以概括画面描述；演员只说原剧本里必要对白；不要把文字显示到画面上。",
      "声音：旁白清晰，环境声和动作音效自然；不要新增无关对白。",
      request ? `导演补充要求：${request}` : "",
    ].filter(Boolean).join("\n");
  }
  return [
    commonHeader,
    compactContinuity,
    compactEmotion,
    compactBlocking,
    visualDirections ? `画面/动作依据（只用于表演和镜头，不要朗读）：\n${visualDirections}` : "",
    exactSpokenText ? `必须完整说出的对白/VO/OS/旁白/系统音：\n${exactSpokenText}` : "本段没有原剧本对白/VO/OS/旁白/系统音，不要新增解释性旁白。",
    `分镜执行：\n${shotPlanText}`,
    "正式剧规则：△/▲ 只转成演员动作、镜头、道具、环境和情绪；只有上方“必须完整说出”的文字需要被说出来。",
    "结尾：停在完整动作、完整表情或完整台词之后，方便下一段连戏；不要截断句子。",
    request ? `导演补充要求：${request}` : "",
  ].filter(Boolean).join("\n");
};

export const buildGuaVideoContents = (packageItems, prompt, options = {}) => {
  const includeAssetId = options?.includeAssetId !== false;
  const provider = normalizeVideoProvider(options?.provider)
    || (options?.overseas === true ? VIDEO_PROVIDER_ENG_GUAVIDEO : VIDEO_PROVIDER_GUAVIDEO);
  if (isDomesticOfficialSeedance(provider)) {
    const contents = prompt ? [{ type: 'text', text: prompt }] : [];
    const counts = { image: 0, video: 0, audio: 0 };
    const seen = new Set();
    // Do not run these inputs through the legacy package normalizer: it drops
    // missing URLs and truncates references before the official API can validate.
    for (const item of Array.isArray(packageItems) ? packageItems : []) {
      const mediaType = getSeedance25ReferenceMediaType(item);
      const url = String(getSeedance25ReferenceUrl(item) || '').trim();
      if (!/^https?:\/\//i.test(url) && !(mediaType === 'image' && /^data:image\//i.test(url))) {
        throw new Error('国内官方 Seedance 参考素材缺少原始 URL，请重新选择原始图片、视频或音频；不能只使用素材库 ID');
      }
      const key = `${mediaType}:${url}`;
      if (seen.has(key)) continue;
      seen.add(key);
      counts[mediaType] += 1;
      contents.push({ type: `${mediaType}_url`, url, role: `reference_${mediaType}` });
    }
    if (counts.image > 9 || counts.video > 3 || counts.audio > 3 || seen.size > 12) {
      throw new Error('国内官方 Seedance 2.0 最多 9 张图片、3 段视频、3 段音频，合计不超过 12 个参考素材');
    }
    if (counts.audio && !counts.image && !counts.video) throw new Error('国内官方 Seedance 音频参考需要同时提供图片或视频');
    return contents;
  }
  const overseas = provider === VIDEO_PROVIDER_ENG_GUAVIDEO
    || provider === VIDEO_PROVIDER_OMINI_SEEDANCE;
  if (overseas) {
    // The new Dreamina v3 account cannot read IDs from the old EngGuaVideo or
    // GuaOmini libraries. Validate before package normalization can drop a row.
    (Array.isArray(packageItems) ? packageItems : []).forEach(requireDreaminaProxyOriginalUrl);
  }
  const contents = [];
  const items = normalizeVideoPackageItems(packageItems);
  const promptWithVoice = applyGuaVoiceBindingPrompt(prompt, [], {
    overseas,
  });
  if (promptWithVoice) contents.push({ type: "text", text: promptWithVoice });
  items.forEach((item) => {
    const isVideo = firstText(item.mediaType).toLowerCase() === "video";
    const content = {
      type: isVideo ? "video_url" : "image_url",
      url: item.url,
      role: isVideo ? "reference_video" : "reference_image",
    };
    if (isVideo) {
      contents.push(content);
      return;
    }
    const guaMeta = getPackageItemGuaMeta(item, {}, provider);
    const assetId = firstText(guaMeta.assetId).replace(/^asset:\/\//i, "");
    const assetStatus = firstText(guaMeta.status).toLowerCase();
    if (includeAssetId && !overseas && assetId && assetStatus === "active") {
      content.assetId = assetId;
    }
    contents.push(content);
  });
  return contents;
};

export const normalizeVideoDuration = (value) => {
  const next = Number(value);
  if (!Number.isFinite(next)) return BOARD_SECONDS;
  return Math.max(4, Math.min(15, Math.round(next)));
};

export const summarizeImageRefs = (imageRefs) => imageRefs.map((item, index) => ({
  index: index + 1,
  role: item.role,
  assetId: item.assetId || "",
  urlType: item.url?.startsWith("data:") ? "base64-data-url" : "url",
  urlPreview: item.url?.startsWith("data:") ? item.url.slice(0, 48) + "..." : item.url,
}));

// ============================================================
// React hook 辅助（依赖 React 的放这里，保持 utils 纯度）
// ============================================================

// useStableEvent：返回稳定引用的回调，避免下游 memo 失效
// 注意：需要 React 环境，放在单独文件 useStableEvent.js 更干净
//  → 见 hooks/useStableEvent.js
